import { castArray, find, uniqBy, values } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { appConfigsApi } from 'api';
import {
  CloseFormikDialogResult,
  DataWrapper,
  DialogProps,
  FormikDialog,
  FormikSelect,
  FormControls,
} from 'components';
import { AppConfigArea, AppConfigKey, QueryKey } from 'enums';
import { ConfigValueField } from 'features/configs/ConfigValueField';
import { usePrevious, useP2PProvidersConfig, useMeta } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AppConfigSchema } from 'types';
import { appConfigUtils, formatUtils } from 'utils';

type ConfigOverride = {
  key: string;
  value: any;
};

type Props = DialogProps<ConfigOverride> & {
  area: AppConfigArea;
  existingKeys: string[];
  providerType?: string;
};

type Values = {
  key: AppConfigKey;
  jsonObjectValue?: string;
  jsonArrayValue?: string;
};

export const ConfigOverrideDialog: React.FC<Props> = ({
  data,
  open,
  onClose,
  area,
  existingKeys,
  providerType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers.config_override_dialog',
  });
  const { t: tCommon } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const prevOpen = usePrevious(open);

  const isNew = useMemo(() => !data?.key, [data]);

  const schemasQueryResult = useQuery(
    [QueryKey.ConfigsSchemas, area],
    appConfigsApi.getAreaSchemas(area),
    {
      enabled: !!area && open,
    },
  );

  const { isP2PProviderAvailable } = useP2PProvidersConfig();
  const { payinOrderP2POverrideKeys } = useMeta();

  const schemas: AppConfigSchema[] = useMemo(() => {
    if (!schemasQueryResult.data) {
      return [];
    }

    return castArray(schemasQueryResult.data).filter(
      (config) =>
        config.p2pProviderType &&
        config.p2pProviderType === providerType &&
        isP2PProviderAvailable(config.p2pProviderType),
    );
  }, [schemasQueryResult.data, providerType, isP2PProviderAvailable]);

  const keyOptions = useMemo(() => {
    const availableSchemas = schemas.filter((schema) => {
      if (existingKeys.includes(schema.key) && schema.key !== data?.key) {
        return false;
      }
      // Hide the order-override key entirely when no allowed keys are configured —
      // nothing can be overridden, so there is no point offering it.
      if (
        schema.key === AppConfigKey.PayinOrderP2POverrides &&
        !payinOrderP2POverrideKeys.length &&
        schema.key !== data?.key
      ) {
        return false;
      }
      return true;
    });

    return uniqBy(availableSchemas, 'key').map((schema) => ({
      label: appConfigUtils.getKeyLabel(schema.key),
      value: schema.key,
    }));
  }, [schemas, existingKeys, data?.key, payinOrderP2POverrideKeys]);

  const keySchema = useMemo(
    () => find(schemas, (schema) => schema.key === selectedKey),
    [schemas, selectedKey],
  );

  const title = useMemo(
    () => (isNew ? t('create_title') : t('edit_title')),
    [t, isNew],
  );

  const defaultInitialValues: Values = useMemo(
    () => ({
      key: '' as AppConfigKey,
      jsonObjectValue: '',
      jsonArrayValue: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] =
    useState<Values>(defaultInitialValues);

  const validationSchema: Yup.ObjectSchema<Partial<Values>> = useMemo(
    () =>
      Yup.object().shape({
        key: Yup.string()
          .oneOf(values(AppConfigKey))
          .required(tCommon('errors.required')),
        jsonObjectValue: Yup.string().test(
          'is-valid-json-object',
          tCommon('errors.is_object'),
          (value) => {
            if (!value) {
              return true;
            }
            const parsed = formatUtils.parseJsonText({ text: value });
            if (!parsed.isJson) {
              return false;
            }
            const parsedValue = JSON.parse(parsed.parsedText);
            return (
              typeof parsedValue === 'object' &&
              parsedValue !== null &&
              !Array.isArray(parsedValue)
            );
          },
        ),
        jsonArrayValue: Yup.string().test(
          'is-valid-json-array',
          tCommon('errors.is_array'),
          (value) => {
            if (!value) {
              return true;
            }
            const parsed = formatUtils.parseJsonText({ text: value });
            if (!parsed.isJson) {
              return false;
            }
            const parsedValue = JSON.parse(parsed.parsedText);
            return Array.isArray(parsedValue);
          },
        ),
      }),
    [tCommon],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok || !result.data) {
        return onClose({ ok: false });
      }

      let value = null;
      if (result.data.values?.jsonObjectValue) {
        const parsedJson = formatUtils.parseJsonText({
          text: result.data.values.jsonObjectValue,
        });
        if (parsedJson.isJson) {
          value = JSON.parse(parsedJson.parsedText);
        }
      } else if (result.data.values?.jsonArrayValue) {
        const parsedJson = formatUtils.parseJsonText({
          text: result.data.values.jsonArrayValue,
        });
        if (parsedJson.isJson) {
          value = JSON.parse(parsedJson.parsedText);
        }
      }

      setInitialValues(defaultInitialValues);
      onClose({
        ok: true,
        data: {
          key: result.data.values.key,
          value,
        },
      });
    },
    [defaultInitialValues, onClose],
  );

  useEffect(() => {
    if (open && !prevOpen) {
      if (data?.key) {
        setSelectedKey(data.key);
        setInitialValues({
          key: data.key as AppConfigKey,
          jsonObjectValue: JSON.stringify(data.value, null, 2),
          jsonArrayValue: '',
        });
      } else {
        setInitialValues(defaultInitialValues);
        setSelectedKey(null);
      }
    }
  }, [data, open, prevOpen, defaultInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <DataWrapper isLoading={schemasQueryResult.isLoading}>
          <FormControls>
            <FormikSelect
              label={t('fields.key')}
              name="key"
              options={keyOptions}
              disabled={!isNew}
              required
              noneOption
              onChange={setSelectedKey}
            />
            {keySchema && (
              <ConfigValueField
                schema={keySchema}
                data={
                  isNew
                    ? { key: keySchema.key }
                    : { id: keySchema.key, key: keySchema.key }
                }
              />
            )}
          </FormControls>
        </DataWrapper>
      )}
    </FormikDialog>
  );
};
